/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useState } from 'react'
import { AdasProgramsReportPages } from '../common/constants'
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks'
import { selectAdasProgramsMetaData, selectSensorComponentRequirementsReportContent, setSensorComponentRequirementsReportContent } from '../../../../../redux/ducks/AdasPrograms/slice'
import { usePostRequest } from '../../../../../api/helper'
import ADAS_PROGRAMS_CONSTANTS_ from "../../constants";
import toast from 'react-hot-toast'
import { MRT_RowSelectionState } from 'material-react-table'
import ArgosFlexbox from '../../../../components/Containers/ArgosFlexbox'
import { MultiPageWizard } from '../../../../components/MultiPageWizard/MultiPageWizard'
import { SelectGreenLineDataSet } from './SelectGreenLineDataSet'
import Loader from '../../../../components/Loader/Loader'
import { SelectGreenLineObjects } from './SelectGreenLineObjects'
import { SelectFormatsAndNames } from '../common/Dialogs/SelectFormatsAndNames'
import useGetUrlParams from '../../../../../hooks/useGetUrlParams'
import { GreenLineDataSetStates } from '../../../SensingSystemArchitectureDesign/types'


interface GreenLineReportWizardProps {
  page: AdasProgramsReportPages
  onSave: (payload: any) => void
  onClickClose: () => void
  isEdit: boolean
}

export interface GreenLineDataSetsData {
  studyId: string
  studyName: string
  isStudyPor: boolean
  greenLineDataSetId: string
  greenLineDataSetName: string
  state: GreenLineDataSetStates
  sensorLocation: string
  sensorModality: string
  sensorId: string
  sensorName: string
  redLineDataSetIdToData: {[redLineDataSetId: string]: { name: string, studyId: string }}
}

interface FetchGreenLineDataSetsResp {
  greenLineDataSets: GreenLineDataSetsData[]
}

/**
 * Renders the wizard to create or edit a green line report.
 */
export const GreenLineReportWizard = (props: GreenLineReportWizardProps) => {
  const dispatch = useAppDispatch()
  const adasProgramMetaData = useAppSelector(selectAdasProgramsMetaData)
  const adasProgramId = adasProgramMetaData?.adasProgramId

  const {page, onSave, onClickClose, isEdit} = props

  const {reportId} = useGetUrlParams()

  const steps = ['Select green line data set', 'Select objects', 'Select formats and name reports']

  const fetchGreenLineDataSetsUrl = ADAS_PROGRAMS_CONSTANTS_.itemDetails.sensorComponentRequirements.endpoints.fetchGreenLineDataSets
  const fetchGreenLineDataSetsHandle = usePostRequest(fetchGreenLineDataSetsUrl)
  const [isLoadingFetchGreenLineDataSets, setIsLoadingFetchGreenLineDataSets] = useState<boolean>(false)

  /**
   * Fetch green line data sets to display in first wizard step
   */
  const fetchGreenLineDataSets = () => {
    if (!adasProgramId) return
    setIsLoadingFetchGreenLineDataSets(true)
    const body = {adasProgramId, page: AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}
    fetchGreenLineDataSetsHandle(body).then((resp: FetchGreenLineDataSetsResp) => {
      setGreenLineDataSets(resp.greenLineDataSets)
    }).catch((err) => {
      console.log(err)
      toast.error('An error occurred while fetching the green line data sets')
    }).finally(() => {
      setIsLoadingFetchGreenLineDataSets(false)
    })
  }

  /**
   * Fetch green line data sets on component mount and ADAS Program change
   */
  useEffect(() => {
    fetchGreenLineDataSets()
  }, [adasProgramId])

  const fetchReportDataHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS_.itemDetails.sensorComponentRequirements.endpoints.getReport)
  const [fetchReportLoading, setFetchReportLoading] = useState(false)

  /**
   * Fetch the report content to prepopulate fields
   */
  const fetchReportData = (reportId: string, adasProgramId: string) => {
    setFetchReportLoading(true)
    const body = {reportId, adasProgramId}
    fetchReportDataHandle(body).then((data) => {
      dispatch(setSensorComponentRequirementsReportContent(data))
    }).catch((e) => {
      console.log(e)
      toast.error("An error occurred while fetching the report data")
    }).finally(() => {
      setFetchReportLoading(false)
    })
  }

  /**
   * Fetch report content if editing on component mount and report or ADAS Program change
   */
  useEffect(() => {
    if (isEdit && reportId && adasProgramId) fetchReportData(reportId, adasProgramId)
  }, [isEdit, reportId, adasProgramId])

  const reportData = useAppSelector(selectSensorComponentRequirementsReportContent)
  const [greenLineDataSets, setGreenLineDataSets] = useState<GreenLineDataSetsData[]>([])
  const [selectedGreenLineDataSets, setSelectedGreenLineDataSets] = useState<MRT_RowSelectionState>({})
  const [selectedGreenLineObjects, setSelectedGreenLineObjects] = useState<MRT_RowSelectionState>({})
  const [selectedGreenLineReports, setSelectedGreenLineReports] = useState<{[reportFormat: string]: string}>({})
  const [reportName, setReportName] = useState<string>('')
  const [reportFormat, setReportFormat] = useState<string>('')

  /** Prepopulate the dialog fields */
  useEffect(() => {
    if (!reportData || !isEdit) return
    setSelectedGreenLineDataSets({[reportData?.selectedReportData?.selectedDataset?.id]: true})
    setSelectedGreenLineObjects(reportData?.selectedReportData?.selectedObjects)
    setReportName(reportData?.selectedReportData?.reportName)
    setReportFormat(reportData?.selectedReportData?.reportFormat)
  }, [reportData])

  /** One data set must be selected */
  const isSelectDatasetValid = Object.keys(selectedGreenLineDataSets).length === 1

  /** At least one object must be selected */
  const isSelectObjectsValid = Object.keys(selectedGreenLineObjects).length > 0

  /**
   * At least one report format must be selected and all selected formats must
   * have a non-empty name
   */
  const isSelectFormatsValid = (reportName && reportFormat) || (
    Object.keys(selectedGreenLineReports).length > 0 &&
    Object.values(selectedGreenLineReports).every((name: string) => name.length > 0)
  )

  /** All steps must be valid to finish */
  const isFinishDisabled = !isSelectDatasetValid || !isSelectObjectsValid || !isSelectFormatsValid

  /**
   * Construct and send payload to save the report
   */
  const onFinish = () => {
    const selectedDatasetId = Object.keys(selectedGreenLineDataSets)[0]
    const selectedDataset = greenLineDataSets.find(dataset => dataset.greenLineDataSetId === selectedDatasetId)
    const studyId = selectedDataset ? selectedDataset.studyId : ''

    if(!selectedDataset) {
      toast.error('Try again, dataset might not be loaded yet.')
      return
    }

    const payload = {
      selectedDataset: { id: selectedDatasetId, studyId },
      selectedObjects: selectedGreenLineObjects,
      reportName,
      reportFormat,
      selectedReports: selectedGreenLineReports,
      isReportSynced: false,
      reportId
    }
    onSave(payload)
  }

  return (
    <ArgosFlexbox vertical style={{height: '100%', flex: 1, overflow: 'auto'}}>
      <MultiPageWizard steps={steps} onFinish={onFinish}
        isNextDisabled={[!isSelectDatasetValid, !isSelectObjectsValid, isFinishDisabled]}
        isFinishLoading={false} isFinishDisabled={isFinishDisabled} onClickClose={onClickClose}
      >
        <Loader isLoading={isLoadingFetchGreenLineDataSets}>
          <SelectGreenLineDataSet
            dataSets={greenLineDataSets}
            selectedDataSets={selectedGreenLineDataSets}
            setSelectedDataSets={setSelectedGreenLineDataSets}
          />
        </Loader>
        <SelectGreenLineObjects
          dataSets={selectedGreenLineDataSets}
          selectedObjects={selectedGreenLineObjects}
          setSelectedObjects={setSelectedGreenLineObjects}
        />
        <SelectFormatsAndNames
          page={page}
          multiSelect={!isEdit}
          dataSets={selectedGreenLineDataSets}
          selectedObjects={selectedGreenLineObjects}
          reportName={reportName}
          setReportName={setReportName}
          reportFormat={reportFormat}
          setReportFormat={setReportFormat}
          selectedReports={selectedGreenLineReports}
          setSelectedReports={setSelectedGreenLineReports}
        />
      </MultiPageWizard>
    </ArgosFlexbox>
  )
}
